import {
  CornerDownRight,
  LoaderCircle,
  MessageCircle,
  Send,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  createOrganizationCalendarScheduleComment,
  deleteOrganizationCalendarScheduleComment,
  getOrganizationCalendarScheduleComments,
  updateOrganizationCalendarScheduleComment,
} from "lib/api/organizationCalendar";
import { formatKoreanDateTime } from "lib/utils/koreanDateTime";

const MAX_COMMENT_LENGTH = 2000;
const MAX_VISUAL_TREE_DEPTH = 6;

function getAuthorInitial(name) {
  return (
    String(name ?? "?")
      .trim()
      .charAt(0) || "?"
  );
}

function formatCommentTime(value) {
  return formatKoreanDateTime(value, { year: "numeric" });
}

function flattenCommentTree(comments) {
  const nodes = new Map();
  const roots = [];

  comments.forEach((comment) => {
    if (!comment?.id) return;
    nodes.set(String(comment.id), { ...comment, children: [] });
  });

  nodes.forEach((comment) => {
    const parentId = String(comment.parentCommentId ?? "");
    const parent = parentId ? nodes.get(parentId) : null;

    if (parent && parent.id !== comment.id) {
      parent.children.push(comment);
    } else {
      roots.push(comment);
    }
  });

  const flattened = [];
  const visited = new Set();
  const visit = (comment, depth) => {
    const id = String(comment.id);
    if (visited.has(id)) return;
    visited.add(id);
    flattened.push({ ...comment, depth });
    comment.children.forEach((child) => visit(child, depth + 1));
  };

  roots.forEach((comment) => visit(comment, 0));
  nodes.forEach((comment) => {
    if (!visited.has(String(comment.id))) visit(comment, 0);
  });

  return flattened;
}

function CommentForm({
  ariaLabel,
  compact = false,
  isSubmitting = false,
  onCancel,
  onChange,
  onSubmit,
  placeholder,
  submitLabel,
  value,
}) {
  return (
    <form
      className={`organizationCalendar__commentForm${
        compact ? " isReply" : ""
      }`}
      onSubmit={onSubmit}
    >
      <textarea
        aria-label={ariaLabel || (compact ? "답글 내용" : "댓글 내용")}
        autoFocus={compact}
        disabled={isSubmitting}
        maxLength={MAX_COMMENT_LENGTH}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (
            event.key === "Enter" &&
            (event.ctrlKey || event.metaKey) &&
            !event.nativeEvent.isComposing
          ) {
            event.preventDefault();
            event.currentTarget.form?.requestSubmit();
          }
        }}
        placeholder={placeholder}
        rows={compact ? 2 : 3}
        value={value}
      />
      <div className="organizationCalendar__commentFormFooter">
        <small>
          {value.length}/{MAX_COMMENT_LENGTH}
        </small>
        <div>
          {onCancel ? (
            <button
              className="organizationCalendar__commentCancel"
              disabled={isSubmitting}
              onClick={onCancel}
              type="button"
            >
              취소
            </button>
          ) : null}
          <button
            className="organizationCalendar__commentSubmit"
            disabled={isSubmitting || !value.trim()}
            type="submit"
          >
            {isSubmitting ? (
              <LoaderCircle
                aria-hidden="true"
                className="isSpinning"
                size={14}
              />
            ) : (
              <Send aria-hidden="true" size={14} />
            )}
            <span>
              {submitLabel || (compact ? "답글 등록" : "댓글 등록")}
            </span>
          </button>
        </div>
      </div>
    </form>
  );
}

export default function OrganizationScheduleComments({
  notify,
  organizationId,
  scheduleId,
}) {
  const requestIdRef = useRef(0);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [replyText, setReplyText] = useState("");
  const [replyingToId, setReplyingToId] = useState("");
  const [editText, setEditText] = useState("");
  const [editingCommentId, setEditingCommentId] = useState("");
  const [submittingTarget, setSubmittingTarget] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const treeComments = useMemo(() => flattenCommentTree(comments), [comments]);

  const loadComments = useCallback(async () => {
    if (!scheduleId || !organizationId) {
      setComments([]);
      setErrorMessage("댓글을 불러올 일정 정보가 없습니다.");
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setIsLoading(true);
    setErrorMessage("");

    const result = await getOrganizationCalendarScheduleComments(scheduleId, {
      organizationId,
    });
    if (requestId !== requestIdRef.current) return;

    setIsLoading(false);
    if (!result.success) {
      setComments([]);
      setErrorMessage(result.message || "댓글을 불러오지 못했습니다.");
      return;
    }

    setComments(
      Array.isArray(result.data?.comments) ? result.data.comments : [],
    );
  }, [organizationId, scheduleId]);

  useEffect(() => {
    void loadComments();
    return () => {
      requestIdRef.current += 1;
    };
  }, [loadComments]);

  const submitComment = async (content, parentCommentId = null) => {
    const normalizedContent = content.trim();
    if (!normalizedContent || submittingTarget) return;

    const target = parentCommentId || "root";
    setSubmittingTarget(target);
    setErrorMessage("");
    const result = await createOrganizationCalendarScheduleComment(scheduleId, {
      organizationId,
      content: normalizedContent,
      parentCommentId,
    });
    setSubmittingTarget("");

    if (!result.success || !result.data?.comment) {
      const message = result.message || "댓글 등록에 실패했습니다.";
      setErrorMessage(message);
      notify?.(message, false);
      return;
    }

    setComments((current) => [...current, result.data.comment]);
    if (parentCommentId) {
      setReplyText("");
      setReplyingToId("");
    } else {
      setCommentText("");
    }
  };

  const updateComment = async (commentId) => {
    const normalizedContent = editText.trim();
    if (!commentId || !normalizedContent || submittingTarget) return;

    const target = `edit:${commentId}`;
    setSubmittingTarget(target);
    setErrorMessage("");
    const result = await updateOrganizationCalendarScheduleComment(
      scheduleId,
      commentId,
      {
        organizationId,
        content: normalizedContent,
      },
    );
    setSubmittingTarget("");

    if (!result.success || !result.data?.comment) {
      const message = result.message || "댓글 수정에 실패했습니다.";
      setErrorMessage(message);
      notify?.(message, false);
      return;
    }

    setComments((current) =>
      current.map((comment) =>
        comment.id === commentId ? result.data.comment : comment,
      ),
    );
    setEditingCommentId("");
    setEditText("");
  };

  const deleteComment = async (comment) => {
    if (
      !comment?.id ||
      comment.children.length > 0 ||
      submittingTarget ||
      !window.confirm("댓글을 삭제하시겠습니까?")
    ) {
      return;
    }

    const target = `delete:${comment.id}`;
    setSubmittingTarget(target);
    setErrorMessage("");
    const result = await deleteOrganizationCalendarScheduleComment(
      scheduleId,
      comment.id,
      { organizationId },
    );
    setSubmittingTarget("");

    if (!result.success) {
      const message = result.message || "댓글 삭제에 실패했습니다.";
      setErrorMessage(message);
      notify?.(message, false);
      return;
    }

    setComments((current) =>
      current.filter((currentComment) => currentComment.id !== comment.id),
    );
    if (editingCommentId === comment.id) {
      setEditingCommentId("");
      setEditText("");
    }
  };

  return (
    <section className="organizationCalendar__comments">
      <div className="organizationCalendar__commentsHeader">
        <div>
          <MessageCircle aria-hidden="true" size={17} />
          <strong>댓글</strong>
          <span>{comments.length}</span>
        </div>
      </div>

      <CommentForm
        isSubmitting={submittingTarget === "root"}
        onChange={setCommentText}
        onSubmit={(event) => {
          event.preventDefault();
          void submitComment(commentText);
        }}
        placeholder="이 일정에 댓글을 남겨보세요."
        value={commentText}
      />

      {errorMessage ? (
        <div className="organizationCalendar__commentError" role="alert">
          <span>{errorMessage}</span>
          {comments.length === 0 ? (
            <button onClick={loadComments} type="button">
              다시 시도
            </button>
          ) : null}
        </div>
      ) : null}

      {isLoading ? (
        <div className="organizationCalendar__commentState" role="status">
          <LoaderCircle aria-hidden="true" className="isSpinning" size={18} />
          <span>댓글을 불러오는 중입니다.</span>
        </div>
      ) : treeComments.length > 0 ? (
        <ul
          aria-label="일정 댓글 목록"
          className="organizationCalendar__commentTree"
          role="tree"
        >
          {treeComments.map((comment) => {
            const visualDepth = Math.min(comment.depth, MAX_VISUAL_TREE_DEPTH);
            const isReplying = replyingToId === comment.id;
            const isEditing = editingCommentId === comment.id;
            const hasReplies = comment.children.length > 0;
            const isDeleting =
              submittingTarget === `delete:${comment.id}`;

            return (
              <li
                aria-level={comment.depth + 1}
                aria-selected="false"
                key={comment.id}
                role="treeitem"
                style={{
                  "--comment-depth": visualDepth,
                }}
              >
                <article className="organizationCalendar__commentItem">
                  {comment.depth > 0 ? (
                    <CornerDownRight
                      aria-hidden="true"
                      className="organizationCalendar__commentBranchIcon"
                      size={15}
                    />
                  ) : null}
                  <span
                    aria-hidden="true"
                    className="organizationCalendar__commentAvatar"
                  >
                    {getAuthorInitial(comment.authorName)}
                  </span>
                  <div className="organizationCalendar__commentContent">
                    <div className="organizationCalendar__commentMeta">
                      <strong>{comment.authorName}</strong>
                      {comment.isMine ? <em>나</em> : null}
                      <time dateTime={comment.createdAt}>
                        {formatCommentTime(comment.createdAt)}
                      </time>
                    </div>
                    <p>{comment.content}</p>
                    <div className="organizationCalendar__commentActions">
                      <button
                        className="organizationCalendar__commentReplyButton"
                        disabled={Boolean(submittingTarget)}
                        onClick={() => {
                          setEditingCommentId("");
                          setEditText("");
                          setReplyingToId(comment.id);
                          setReplyText("");
                          setErrorMessage("");
                        }}
                        type="button"
                      >
                        답글
                      </button>
                      {comment.isMine ? (
                        <>
                          <button
                            className="organizationCalendar__commentEditButton"
                            disabled={Boolean(submittingTarget)}
                            onClick={() => {
                              setReplyingToId("");
                              setReplyText("");
                              setEditingCommentId(comment.id);
                              setEditText(comment.content);
                              setErrorMessage("");
                            }}
                            type="button"
                          >
                            수정
                          </button>
                          <button
                            className="organizationCalendar__commentDeleteButton"
                            disabled={Boolean(submittingTarget) || hasReplies}
                            onClick={() => void deleteComment(comment)}
                            title={
                              hasReplies
                                ? "답글이 등록된 댓글은 삭제할 수 없습니다."
                                : "댓글 삭제"
                            }
                            type="button"
                          >
                            {isDeleting ? "삭제 중" : "삭제"}
                          </button>
                        </>
                      ) : null}
                    </div>
                  </div>
                </article>

                {isEditing ? (
                  <CommentForm
                    ariaLabel="댓글 수정 내용"
                    compact
                    isSubmitting={
                      submittingTarget === `edit:${comment.id}`
                    }
                    onCancel={() => {
                      setEditingCommentId("");
                      setEditText("");
                    }}
                    onChange={setEditText}
                    onSubmit={(event) => {
                      event.preventDefault();
                      void updateComment(comment.id);
                    }}
                    placeholder="수정할 댓글 내용을 입력하세요."
                    submitLabel="변경 사항 저장"
                    value={editText}
                  />
                ) : null}

                {isReplying ? (
                  <CommentForm
                    compact
                    isSubmitting={submittingTarget === comment.id}
                    onCancel={() => {
                      setReplyingToId("");
                      setReplyText("");
                    }}
                    onChange={setReplyText}
                    onSubmit={(event) => {
                      event.preventDefault();
                      void submitComment(replyText, comment.id);
                    }}
                    placeholder={`${comment.authorName}님에게 답글 남기기`}
                    value={replyText}
                  />
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="organizationCalendar__commentState isEmpty">
          <MessageCircle aria-hidden="true" size={19} />
          <span>첫 댓글을 남겨보세요.</span>
        </div>
      )}
    </section>
  );
}
